'use client'

import * as React from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'

// Starts the preview with sound; browsers that block unmuted autoplay get a muted preview instead.
const playWithSound = (video) => {
    if (!video) return

    video.muted = false
    video.play()?.catch(() => {
        video.muted = true
        video.play()?.catch(() => {})
    })
}

const HoverableMediaPreview = React.forwardRef(
    (
        {
            src,
            mediaType = 'image',
            className,
            thumbnailClassName,
            expandedClassName,
            disabled = false,
            children,
            previewContent,
            tooltipSize = 'large', // 'small', 'medium', 'large'
            aspectRatio, // width / height of the media; when given, the preview is kept inside the viewport
            ...props
        },
        ref,
    ) => {
        const [showTooltip, setShowTooltip] = React.useState(false)
        const [isTooltipVisible, setIsTooltipVisible] = React.useState(false)
        const [mousePosition, setMousePosition] = React.useState({ x: 0, y: 0 })
        const timeoutRef = React.useRef()
        const animationTimeoutRef = React.useRef()

        const handleMouseEnter = React.useCallback(() => {
            if (disabled) return

            clearTimeout(timeoutRef.current)
            clearTimeout(animationTimeoutRef.current)
            timeoutRef.current = setTimeout(() => {
                setShowTooltip(true)
                requestAnimationFrame(() => {
                    setIsTooltipVisible(true)
                })
            }, 300) // 300ms delay
        }, [disabled])

        const handleMouseLeave = React.useCallback(() => {
            clearTimeout(timeoutRef.current)
            clearTimeout(animationTimeoutRef.current)
            setIsTooltipVisible(false)
            animationTimeoutRef.current = setTimeout(() => {
                setShowTooltip(false)
            }, 100) // Match animation duration
        }, [])

        const handleMouseMove = React.useCallback(
            (e) => {
                if (!disabled) {
                    setMousePosition({ x: e.clientX, y: e.clientY })
                }
            },
            [disabled],
        )

        React.useEffect(() => {
            return () => {
                clearTimeout(timeoutRef.current)
                clearTimeout(animationTimeoutRef.current)
            }
        }, [])

        if (!src) return children || null

        const tooltipSizes = {
            small: { width: 300, height: 300 },
            medium: { width: 450, height: 450 },
            large: { width: 600, height: 600 },
        }

        const size = tooltipSizes[tooltipSize]
        // The enlarged media plus the p-2 padding and border of its frame
        const previewHeight = aspectRatio ? size.width / aspectRatio + 18 : size.height
        const centeredTop = mousePosition.y - previewHeight / 2
        const top = aspectRatio
            ? Math.max(8, Math.min(centeredTop, window.innerHeight - previewHeight - 8))
            : centeredTop

        const Thumbnail = (
            <div
                ref={ref}
                className={cn(
                    'tw:transition-all tw:duration-200 tw:ease-out',
                    !disabled && 'tw:hover:tw:brightness-110 tw:hover:tw:scale-[1.02]',
                    thumbnailClassName,
                    className,
                )}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onMouseMove={handleMouseMove}
                {...props}
            >
                {children}
            </div>
        )

        const Tooltip = showTooltip && (
            <div
                className={cn(
                    'tw:fixed tw:pointer-events-none tw:z-[9999] tw:transition-opacity tw:duration-150 tw:ease-out',
                    isTooltipVisible ? 'tw:opacity-100' : 'tw:opacity-0',
                )}
                style={{
                    left: mousePosition.x + 15, // Offset from cursor
                    top,
                    transform:
                        mousePosition.x + size.width + 15 > window.innerWidth
                            ? 'translateX(-100%) translateX(-30px)' // Flip to left side if too close to right edge
                            : 'none',
                }}
            >
                {previewContent ? (
                    previewContent
                ) : (
                    <div className="tw:shadow-2xl tw:rounded-xs tw:overflow-hidden tw:p-2 tw:border tw:bg-primary tw:flex tw:flex-col tw:gap-2">
                        {mediaType === 'video' ? (
                            <video
                                ref={playWithSound}
                                src={src}
                                className={cn('tw:object-cover tw:block tw:max-w-none', expandedClassName)}
                                style={{ width: size.width }}
                                autoPlay
                                loop
                                playsInline
                            />
                        ) : (
                            <img
                                src={src}
                                alt=""
                                className={cn('tw:object-cover tw:block tw:max-w-none', expandedClassName)}
                                style={{ width: size.width }}
                            />
                        )}
                    </div>
                )}
            </div>
        )

        if (disabled) return Thumbnail

        return (
            <>
                {Thumbnail}
                {typeof window !== 'undefined' && Tooltip && createPortal(Tooltip, document.body)}
            </>
        )
    },
)

HoverableMediaPreview.displayName = 'HoverableMediaPreview'

export { HoverableMediaPreview }
